import { CsvError, parse } from 'csv-parse/browser/esm';
import { Options as CSVOptions, stringify } from 'csv-stringify';
import readXlsxFile from 'read-excel-file';
import writeXlsxFile, { Schema } from 'write-excel-file';

interface SpreadsheetFunctions {
  parse: (blob: Blob) => Promise<Record<string, string>[]>;
  stringify: <T extends object = Record<string, object>>(
    records: Array<T>,
    columnOrder: string[] | { key: string; hide: boolean }[]
  ) => Promise<Blob>;
}

export enum SPREADSHEET_MIME_TYPES {
  XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  CSV = 'text/csv',
}

/**
 *
 * @param {Blob} blob
 * @return {Promise<string | ArrayBuffer>}
 */
export async function readBlob(
  blob: Blob,
  { binary = false } = {}
): Promise<string | ArrayBuffer | null | undefined> {
  const reader = new FileReader();

  return new Promise((resolve) => {
    reader.addEventListener('load', (e) => resolve(e.target?.result));
    if (!binary) {
      reader.readAsText(blob);
    } else {
      reader.readAsArrayBuffer(blob);
    }
  });
}

/**
 *
 * @param {Blob} blob
 * @return {Promise<Record<string, string>[]>}
 */
export async function parseSpreadsheetBlobCSV<T = Record<string, string>[]>(
  blob: Blob
): Promise<T[]> {
  const textContent = (await readBlob(blob)) as string;
  return new Promise((resolve, reject) => {
    if (!textContent) {
      reject(new Error('Text content is empty'));
    }
    try {
      parse(
        textContent,
        { columns: true },
        (err: CsvError | undefined, records: T[]) => {
          if (err) {
            reject(err);
          } else {
            resolve(records);
          }
        }
      );
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      reject(new Error('There was an error parsing the file: ' + message));
    }
  });
}

export async function parseSpreadsheetBlobXLSX(
  blob: Blob
): Promise<Record<string, string>[]> {
  const rows = (await readXlsxFile(blob as File, {
    sheet: 'data',
  })) as unknown[][];
  if (rows.length < 2) return [];

  const [headers, ...dataRows] = rows;
  return dataRows.map((row: unknown[]) => {
    const record: Record<string, string> = {};
    (headers as unknown[]).forEach((header: unknown, i: number) => {
      record[String(header)] = row[i] != null ? String(row[i]) : '';
    });
    return record;
  });
}

export async function stringifyToSpreadsheetCSV<
  T extends object = Record<string, object>,
>(records: Array<T>, columnOrder: CSVOptions['columns']): Promise<Blob> {
  const csvContent: BlobPart = await new Promise((resolve, reject) =>
    stringify(
      records,
      {
        header: true,
        columns: columnOrder,
      },
      (err, output) => {
        if (err) {
          reject(err);
        } else {
          resolve(output);
        }
      }
    )
  );

  return new Blob([csvContent], { type: SPREADSHEET_MIME_TYPES.CSV });
}

export async function stringifyToSpreadsheetXLSX<T extends object>(
  records: Array<T>,
  columnOrder: string[] | { key: string; hide: boolean }[]
): Promise<Blob> {
  const schema: Schema<T> = columnOrder.map(
    (c: string | { key: string; hide: boolean }) => {
      const key = typeof c !== 'string' ? c.key : c;
      const hide = typeof c !== 'string' ? c.hide : false;
      return {
        column: key,
        type: String,
        value: (record: T) => {
          const val = (record as Record<string, unknown>)[key];
          return val != null ? String(val) : '';
        },
        width: hide ? 0.1 : 20,
      };
    }
  );

  return writeXlsxFile(records, { schema, sheet: 'data' });
}

const mimeTypeToSpreasheetFns: Record<
  SPREADSHEET_MIME_TYPES,
  SpreadsheetFunctions
> = {
  [SPREADSHEET_MIME_TYPES.CSV]: {
    parse: parseSpreadsheetBlobCSV,
    stringify: stringifyToSpreadsheetCSV,
  },
  [SPREADSHEET_MIME_TYPES.XLSX]: {
    stringify: stringifyToSpreadsheetXLSX,
    parse: parseSpreadsheetBlobXLSX,
  },
};

/**
 *
 * @param {File} file
 * @return {Promise<Record<string, string>[]>}
 */
export async function parseSpreadsheetFromFile(
  file: File
): Promise<Record<string, string>[]> {
  const getRecordsFromSpreadsheetBlob =
    mimeTypeToSpreasheetFns[file.type as SPREADSHEET_MIME_TYPES]?.parse;

  if (!getRecordsFromSpreadsheetBlob) {
    throw new Error(`Spreadsheet MIME type not supported: ${file.type}`);
  }
  return await getRecordsFromSpreadsheetBlob(file);
}

export async function stringifyToSpreadsheet<
  T extends object = Record<string, object>,
>(
  records: Array<T>,
  columnOrder: string[] | { key: string; hide: boolean }[],
  type: SPREADSHEET_MIME_TYPES = SPREADSHEET_MIME_TYPES.XLSX
) {
  const stringifyToSpreadsheetFn = mimeTypeToSpreasheetFns[type]?.stringify;
  if (!stringifyToSpreadsheetFn) {
    throw new Error(`Spreadsheet MIME type not supported: ${type}`);
  }
  return await stringifyToSpreadsheetFn(records, columnOrder);
}

export function downloadBlob(blob: Blob, fileName: string): void {
  const downloadUrl = URL.createObjectURL(blob);
  const anchorElement = document.createElement('a');
  anchorElement.href = downloadUrl;
  anchorElement.download = fileName;
  anchorElement.target = '_blank';
  anchorElement.click();

  setTimeout(() => URL.revokeObjectURL(downloadUrl), 2000);
}
